import '@cfa/react-components/dist/styles.min.css';
// Load the grid stylesheet after the component library stylesheet
import '@cfa/react-components/dist/grid.min.css';
import '@cfa/react-core/index.css';

import { Dashboard as DashboardIcon } from '@cfa/brand-icons';
import { leaveBreadcrumb, notifyBugsnag } from 'cfa-react-logging';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import authClient from './authClient';
import Cart from './cart';
import AddNewCard from './components/AddNewCard';
import CMTBannerMessage from './components/CMTBannerMessage/index.tsx';
import Eula from './components/Eula/Eula';
import Footer from './components/Footer/Footer';
import FullScreenError from './components/FullScreenError';
import Icon from './components/Icon';
import InitialLoad from './components/InitialLoad/InitialLoad';
import LoadingPage from './components/LoadingPage';
import Logo from './components/Logo/Logo';
import CartLink from './components/NavComponents/CartLink';
import RightDesktop from './components/NavComponents/RightDesktop';
import UserLocation from './components/NavComponents/UserLocation';
import Navigation from './components/Navigation/Navigation';
import MoreTab from './components/Tabs/MoreTab';
import NavTab from './components/Tabs/NavTab';
import Tabs from './components/Tabs/Tabs';
import ooeConstants from './constants';
import DeviceStatusMessage from './device/components/DeviceStatusMessage';
import { selectDisplayMessage } from './device/selectors';
import DiagnosticMessages from './diagnosticMessages';
import { actions as dmActions } from './diagnosticMessages/reducer.ts';
import { DiagnosticMessageType } from './diagnosticMessages/types';
import FloatingMenu from './floatingMenu';
import { actions as orderActions } from './order/reducers';
import { actions as guestActions } from './guest/reducer';
import { logPageView } from './services/analytics';
import * as ldService from './services/launchDarkly.ts';
import { LDFlags, Token } from './shared/types.ts';
import theme from './theme';
import { AppDispatch } from './types/store';
import { actions as userActions } from './user/reducer';
import { selectIsSelectedLocationReadOnly, selectLoadingLocations } from './user/selectors';
import { checkEulaMessageOnLoad } from './user/thunks';
import { mapTokenToUserInfo } from './user/utils.ts';
import usePrevious from './util/customHooks';
import lazyWithRetry from './util/lazyWithRetry';
import { getQueryVariable } from './util/routerHelpers';

const Dashboard = lazyWithRetry(() => import('./dashboard'));
const Guest = lazyWithRetry(() => import('./guest'));
const EventDetails = lazyWithRetry(() => import('./eventDetails'));
const Menu = lazyWithRetry(() => import('./menu'));
const Payment = lazyWithRetry(() => import('./payment'));
const Submit = lazyWithRetry(() => import('./submit'));
const EditOrderError = lazyWithRetry(() => import('./submit/EditOrderError'));
const DuplicateOrderError = lazyWithRetry(() => import('./submit/DuplicateOrderError'));
const More = lazyWithRetry(() => import('./more'));

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const loadingLocations = useSelector(selectLoadingLocations);
  const isReadOnlyLocation = useSelector(selectIsSelectedLocationReadOnly);
  const displayDeviceMessage = useSelector(selectDisplayMessage);

  const [signedIn, setSignedIn] = useState(false);

  const location = useLocation<{ modal: boolean }>();
  let previousLocation = location;
  const history = useHistory();

  const { showLdEnabledModal = false, messagingBanner = { messages: [] } } = useFlags<LDFlags>();
  const ldClient = useLDClient();

  // Initialize LaunchDarkly service
  useEffect(() => {
    if (ldClient) {
      ldService.setClient(ldClient);
      ldClient.on('error', (error) => {
        leaveBreadcrumb('LaunchDarkly Client Error', {
          message: 'An error occurred with the LaunchDarkly client',
          error,
        });
      });
    }
  }, [ldClient]);

  useEffect(() => {
    if (showLdEnabledModal) {
      dispatch(
        dmActions.addMessage({
          message: { dismissible: true, text: 'LD Flag Enabled', type: DiagnosticMessageType.warning },
        }),
      );
    }
  }, [showLdEnabledModal]);

  useEffect(() => {
    (async () => {
      authClient.authStateManager.subscribe((authState) => {
        const user = authState.accessToken?.claims as Token | undefined;
        if (!user) {
          return;
        }
        const userInfo = mapTokenToUserInfo(user);
        const ldContext = ldClient?.getContext() ?? {};

        ldClient?.identify({
          ...ldContext,
          kind: 'multi',
          user: {
            key: userInfo.cfaGuid,
            audience: userInfo.cfaAud,
            email: userInfo.email,
            role: userInfo.type,
          },
        });
        dispatch(userActions.updateUserInfo({ user: userInfo }));
        dispatch(checkEulaMessageOnLoad());
      });

      if (authClient.isLoginRedirect()) {
        try {
          await authClient.handleRedirect();
        } catch (err) {
          notifyBugsnag({
            grouping: ooeConstants.BUGSNAG_ERRORCLASS_OKTA,
            message: 'Handle redirect error',
            error: err,
          });
        }
      } else if (!(await authClient.isAuthenticated())) {
        try {
          await authClient.signInWithRedirect();
        } catch (err) {
          notifyBugsnag({
            grouping: ooeConstants.BUGSNAG_ERRORCLASS_OKTA,
            message: 'Sign in redirect error',
            error: err,
          });
        }
      }

      authClient.authStateManager.updateAuthState();
      setSignedIn(true);

      if (getQueryVariable('phoneNumber')) {
        dispatch(
          guestActions.setGuestFormField({ field: 'phone', value: getQueryVariable('phoneNumber') || '' }),
        );
      }

      if (getQueryVariable('callId')) {
        const callSource = getQueryVariable('callSource') || ooeConstants.VCA_INKTEL;
        dispatch(
          orderActions.addExternalOrderId({
            orderId: getQueryVariable('callId') || '',
            externalOrderSource: callSource,
          }),
        );
      }

      history.listen((loc) => {
        logPageView(loc.pathname);
      });
    })();
  }, []);

  const prevProps = usePrevious({ location });
  const prevPropsLocation = prevProps?.location;

  useEffect(() => {
    if (
      history?.action !== 'POP' &&
      (!prevPropsLocation?.state || !prevPropsLocation?.state?.modal) &&
      prevProps?.location
    ) {
      previousLocation = prevProps?.location;
    }
  });

  const { pathname } = location;
  const isModal = !!(location.state && location.state.modal);

  return (
    <ThemeProvider theme={theme}>
      <>
        {signedIn && !loadingLocations && !ooeConstants.IS_IN_CYPRESS_TEST && <Eula />}
        {((signedIn && !loadingLocations) || window.location.pathname === '/error') && (
          <div style={{ minHeight: '97vh' }}>
            {displayDeviceMessage && <DeviceStatusMessage />}
            {messagingBanner.messages.length > 0 &&
              messagingBanner.messages.map((message, index) => (
                <CMTBannerMessage key={index} message={message.messageText} type={message.type} />
              ))}
            {isReadOnlyLocation && (
              <CMTBannerMessage message="This store is closed for remodel." type="alert" />
            )}
            <Navigation
              leftDesktop={<Logo className="" />}
              leftMobile={<UserLocation />}
              pathname={pathname}
              rightDesktop={<RightDesktop />}
              rightMobile={<CartLink />}
            >
              <Tabs>
                {!isReadOnlyLocation && (
                  <>
                    <NavTab activeOnlyWhenExact icon={<Icon>man</Icon>} title="Guest" to="/" />
                    <NavTab activeOnlyWhenExact icon={<Icon>calendar</Icon>} title="Details" to="/event" />
                    <NavTab activeOnlyWhenExact icon={<Icon>sandwich</Icon>} title="Menu" to="/menu" />
                    <NavTab activeOnlyWhenExact icon={<Icon>payment</Icon>} title="Payment" to="/payment" />
                  </>
                )}
                <NavTab
                  activeOnlyWhenExact
                  icon={<DashboardIcon size="xl" />}
                  title="Dashboard"
                  to="/dashboard"
                />
                <MoreTab icon={<Icon>menu</Icon>} title="More" to="/more" />
              </Tabs>
            </Navigation>
            <DiagnosticMessages />
            <Suspense fallback={<LoadingPage />}>
              <Switch location={isModal ? previousLocation : location}>
                {isReadOnlyLocation ? (
                  <>
                    <Route exact path="/dashboard" render={() => <Dashboard />} />
                    <Route exact path="/more" render={() => <More />} />
                    <Route exact path="/error/edit-failed" render={() => <EditOrderError />} />
                    <Route exact path="/error/duplicate-order" render={() => <DuplicateOrderError />} />
                    <Route component={FullScreenError} exact path="/error" />
                    <Redirect to="/dashboard" />
                  </>
                ) : (
                  <>
                    <Route exact path="/" render={() => <Guest />} />
                    <Route exact path="/event" render={() => <EventDetails />} />
                    <Route exact path="/menu" render={() => <Menu />} />
                    <Route exact path="/payment" render={() => <Payment />} />
                    <Route exact path="/dashboard" render={() => <Dashboard />} />
                    <Route exact path="/submit" render={() => <Submit />} />
                    <Route exact path="/error/edit-failed" render={() => <EditOrderError />} />
                    <Route exact path="/error/duplicate-order" render={() => <DuplicateOrderError />} />
                    <Route exact path="/more" render={() => <More />} />
                    <Route component={FullScreenError} exact path="/error" />
                  </>
                )}
              </Switch>
            </Suspense>

            <AddNewCard />
            <FloatingMenu />
            <Cart />
          </div>
        )}

        {(!signedIn || loadingLocations) && window.location.pathname !== '/error' && (
          <InitialLoad className="" />
        )}
        <Footer>{`Version ${ooeConstants.APP_VERSION}`}</Footer>
      </>
    </ThemeProvider>
  );
};

export default App;
